import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import {
  Robot, Megaphone, ShoppingCart, Sparkle, ArrowRight, Spinner, CheckCircle,
} from "@phosphor-icons/react";

/**
 * MyTasksWidget — dashboard panel for non-admin users. Lists pending
 * Alendra tasks assigned to the user's role(s). Each item links to /mis-tareas.
 */
const SOURCE_META = {
  inventory: { label: "Inventario", icon: Robot, color: "text-[#002FA7]" },
  marketing: { label: "Marketing", icon: Megaphone, color: "text-pink-600" },
  purchasing: { label: "Compras", icon: ShoppingCart, color: "text-emerald-600" },
};

const STATUS_META = {
  approved: { label: "Por hacer", color: "bg-blue-100 text-blue-700" },
  in_progress: { label: "En progreso", color: "bg-amber-100 text-amber-700" },
};

export default function MyTasksWidget() {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    api.get("/alendra/tasks", { params: { mine: true } })
      .then((r) => {
        if (cancelled) return;
        const active = (r.data?.items || []).filter(
          (t) => t.status === "approved" || t.status === "in_progress",
        );
        setTasks(active);
      })
      .catch(() => { if (!cancelled) setTasks([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  if (loading) {
    return (
      <div className="border border-zinc-200 rounded-lg p-6 bg-white text-center" data-testid="mytasks-widget-loading">
        <Spinner size={18} className="animate-spin inline text-[#002FA7] mr-2" />
        <span className="text-sm text-zinc-500">Cargando tus tareas…</span>
      </div>
    );
  }

  if (tasks.length === 0) {
    return (
      <div
        className="border border-dashed border-emerald-300 rounded-lg p-6 bg-gradient-to-br from-emerald-50 to-white flex items-center gap-4"
        data-testid="mytasks-widget-empty"
      >
        <div className="w-12 h-12 rounded-full bg-emerald-500 text-white flex items-center justify-center shrink-0">
          <CheckCircle size={24} weight="fill" />
        </div>
        <div className="flex-1">
          <h3 className="font-bold text-base">¡Sin tareas pendientes!</h3>
          <p className="text-sm text-zinc-600">Cuando Alendra AI asigne acciones para tu área, aparecerán aquí.</p>
        </div>
      </div>
    );
  }

  const preview = tasks.slice(0, 6);

  return (
    <div className="border border-zinc-200 rounded-lg bg-white overflow-hidden" data-testid="mytasks-widget">
      <div className="flex items-center justify-between px-5 py-3 border-b border-zinc-100 bg-gradient-to-r from-[#002FA7]/5 to-transparent">
        <div className="flex items-center gap-2">
          <Sparkle size={18} weight="fill" className="text-[#002FA7]" />
          <h3 className="font-bold text-base">Tus estrategias y tareas</h3>
          <span className="text-xs font-mono-data text-zinc-500 ml-1">{tasks.length} activa{tasks.length === 1 ? "" : "s"}</span>
        </div>
        <Link
          to="/mis-tareas"
          className="text-sm text-[#002FA7] hover:underline inline-flex items-center gap-1"
          data-testid="mytasks-widget-link"
        >
          Ver todas <ArrowRight size={14} />
        </Link>
      </div>
      <ul className="divide-y divide-zinc-100">
        {preview.map((t) => {
          const src = SOURCE_META[t.source] || { label: t.source, icon: Sparkle, color: "text-zinc-600" };
          const stat = STATUS_META[t.status] || { label: t.status, color: "bg-zinc-100 text-zinc-600" };
          const Icon = src.icon;
          return (
            <li key={t.id} className="px-5 py-3 hover:bg-zinc-50 transition" data-testid={`mytasks-item-${t.id}`}>
              <Link to="/mis-tareas" className="flex items-start gap-3">
                <Icon size={20} weight="duotone" className={`${src.color} shrink-0 mt-0.5`} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded ${stat.color}`}>{stat.label}</span>
                    <span className="text-[10px] uppercase tracking-wider font-semibold text-zinc-400">{src.label}</span>
                  </div>
                  <p className="text-sm text-zinc-800 mt-1 line-clamp-2">{t.text || t.title || "Sin descripción"}</p>
                  {t.product_name && (
                    <p className="text-xs text-zinc-500 mt-0.5">Producto: <span className="font-medium">{t.product_name}</span></p>
                  )}
                </div>
              </Link>
            </li>
          );
        })}
      </ul>
      {tasks.length > preview.length && (
        <div className="px-5 py-2.5 bg-zinc-50 border-t border-zinc-100 text-center">
          <Link to="/mis-tareas" className="text-xs text-[#002FA7] hover:underline" data-testid="mytasks-widget-more">
            Ver {tasks.length - preview.length} más…
          </Link>
        </div>
      )}
    </div>
  );
}
